import type {
  AttainmentTrendPeriod,
  AttainmentTrendResult,
  PeriodAverageScoreDeltas,
  PeriodAverageScores,
} from "@repo/app-types/entities/attainmentTrend";
import {
  buildTeammateDisplayNames,
  type Teammate,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { recordEntries } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { ChartSkeleton, skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import { IconArrowDownRight, IconArrowUpRight } from "@tabler/icons-react";
import { useMemo, useState, type FC } from "react";
import { useNavigate, useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { appPaths } from "../../../app/appPaths.js";
import { OutcomeRatePill } from "../outcomeRatePill.js";
import {
  computeScoreAxisRange,
  ScoreMovementScatterChart,
  type MovementCategory,
  type ScoreMovementPoint,
} from "../scoreMovementScatterChart.js";

interface TeammateScoreMovement {
  id: string;
  name: string;
  startScore: number;
  currentScore: number;
  startScorePct: number;
  currentScorePct: number;
  deltaPct: number;
}

function getMovementCategory(deltaPct: number): MovementCategory {
  if (deltaPct > 2) return "improving";
  if (deltaPct < -2) return "declining";
  return "flat";
}

const PERIOD_OPTIONS: { value: AttainmentTrendPeriod; label: string }[] = [
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_14_days", label: "Last 14 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "last_90_days", label: "Last 90 days" },
  { value: "last_180_days", label: "Last 180 days" },
];

interface DashboardOutcomeAttainmentTrendCardProps {
  readonly outcomeAttainment: AttainmentTrendResult | undefined;
  readonly teammates: readonly Teammate[];
  readonly isLoading: boolean;
  readonly showPeriodDropdown: boolean;
  readonly outcomeAverageScore: PeriodAverageScores | undefined;
  readonly outcomeAverageScoreDelta: PeriodAverageScoreDeltas | undefined;
  readonly outcomeGreenLabel: string | undefined;
  readonly onSelectedPeriodChange?: (period: AttainmentTrendPeriod) => void;
}

export const DashboardOutcomeAttainmentTrendCard: FC<
  DashboardOutcomeAttainmentTrendCardProps
> = ({
  outcomeAttainment,
  teammates,
  isLoading,
  showPeriodDropdown,
  outcomeAverageScore,
  outcomeAverageScoreDelta,
  outcomeGreenLabel,
  onSelectedPeriodChange,
}) => {
  const navigate = useNavigate();
  const { playbookId } = useParams();
  const [selectedPeriod, setSelectedPeriod] =
    useState<AttainmentTrendPeriod>("last_30_days");
  const handleSelectedPeriodChange = (period: AttainmentTrendPeriod) => {
    setSelectedPeriod(period);
    onSelectedPeriodChange?.(period);
  };

  const [visibleCategories, setVisibleCategories] = useState<
    Record<MovementCategory, boolean>
  >({
    improving: true,
    flat: true,
    declining: true,
  });

  const effectiveAverageScore = outcomeAverageScore?.[selectedPeriod];
  const effectiveDelta = outcomeAverageScoreDelta?.[selectedPeriod];

  const toggleCategory = (category: MovementCategory) => {
    setVisibleCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const teammatesMap = useMemo(() => {
    return teammates.reduce<Record<TeammateId, Teammate>>((acc, teammate) => {
      acc[teammate.id] = teammate;
      return acc;
    }, {});
  }, [teammates]);

  const teammateScoreMovements =
    useMemo((): readonly TeammateScoreMovement[] => {
      const result: TeammateScoreMovement[] = [];

      for (const [teammateId, trends] of recordEntries(
        outcomeAttainment ?? {},
      )) {
        const period = trends[selectedPeriod];
        if (!period) continue;

        const startScore = period.starting_score;
        const currentScore = period.current_score;
        if (startScore === null || currentScore === null) continue;

        const teammate = teammatesMap[teammateId];
        const displayName = teammate
          ? buildTeammateDisplayNames(teammate).primaryDisplayName
          : teammateId;

        const startScorePct = Math.round(startScore * 100);
        const currentScorePct = Math.round(currentScore * 100);

        result.push({
          id: teammateId,
          name: displayName,
          startScore,
          currentScore,
          startScorePct,
          currentScorePct,
          deltaPct: currentScorePct - startScorePct,
        });
      }

      return result;
    }, [outcomeAttainment, selectedPeriod, teammatesMap]);

  const categoryStats = useMemo(() => {
    const stats = {
      improving: { count: 0, totalDelta: 0 },
      flat: { count: 0, totalDelta: 0 },
      declining: { count: 0, totalDelta: 0 },
    };

    for (const t of teammateScoreMovements) {
      const category = getMovementCategory(t.deltaPct);
      stats[category].count++;
      stats[category].totalDelta += t.deltaPct;
    }

    return {
      improving: {
        count: stats.improving.count,
        avgDelta:
          stats.improving.count > 0
            ? Math.round(stats.improving.totalDelta / stats.improving.count)
            : 0,
      },
      flat: {
        count: stats.flat.count,
      },
      declining: {
        count: stats.declining.count,
        avgDelta:
          stats.declining.count > 0
            ? Math.round(stats.declining.totalDelta / stats.declining.count)
            : 0,
      },
    };
  }, [teammateScoreMovements]);

  const { axisMin, axisMax, axisTicks } = useMemo(
    () => computeScoreAxisRange(teammateScoreMovements, "yes_no"),
    [teammateScoreMovements],
  );

  const chartPoints = useMemo((): readonly ScoreMovementPoint[] => {
    return teammateScoreMovements
      .map((teammate): ScoreMovementPoint | null => {
        const category = getMovementCategory(teammate.deltaPct);
        if (!visibleCategories[category]) return null;

        return {
          id: teammate.id,
          startScore: teammate.startScore,
          currentScore: teammate.currentScore,
          category,
          tooltipContent: `${teammate.name}\nBefore: ${teammate.startScorePct}%\nAfter: ${teammate.currentScorePct}%\nDelta: ${teammate.deltaPct >= 0 ? "+" : ""}${teammate.deltaPct}pts`,
          onClick: () => {
            if (playbookId) {
              navigate(
                appPaths.teammate.build({
                  playbookId,
                  teammateId: teammate.id,
                }),
              );
            }
          },
        };
      })
      .filter((p): p is ScoreMovementPoint => p !== null);
  }, [teammateScoreMovements, visibleCategories, navigate, playbookId]);

  return (
    <div className="rounded-lg border border-border-light bg-white p-4">
      {/* Controls row */}
      <div className="mb-4 flex items-center gap-2">
        <div className="flex items-center gap-2">
          {/* Average score pill */}
          <OutcomeRatePill
            size={Size.MEDIUM}
            outcomeRate={effectiveAverageScore}
            label={outcomeGreenLabel}
            isLoading={isLoading}
          />

          {/* Delta with previous period */}
          {isLoading ? (
            <div className={skeletonStyle({ class: "h-5 w-10 rounded-sm" })} />
          ) : (
            effectiveDelta != null &&
            (() => {
              const isFlat = Math.abs(effectiveDelta) < 0.01;
              return (
                <span
                  className={twMerge(
                    "text-sm font-medium",
                    isFlat
                      ? "text-muted-foreground"
                      : effectiveDelta > 0
                        ? "text-success"
                        : "text-red-500",
                  )}
                >
                  {isFlat
                    ? "flat"
                    : `${effectiveDelta > 0 ? "+" : ""}${Math.round(effectiveDelta * 100)} pts`}
                </span>
              );
            })()
          )}
        </div>
        {/* Separator */}
        <div className="mx-1 h-4 w-px bg-gray-200" />
        {/* Improving button */}
        <Button
          variant={ButtonVariant.Secondary}
          onClick={() => toggleCategory("improving")}
          className={twMerge(
            "shrink-0 font-normal transition-opacity",
            !visibleCategories.improving && "opacity-50",
          )}
        >
          <span className="inline-flex items-center">
            {categoryStats.improving.count}
            <IconArrowUpRight className="mx-0.5 h-3 w-3 text-success" />
            {categoryStats.improving.count > 0 &&
              ` (+${categoryStats.improving.avgDelta} pts avg)`}
          </span>
        </Button>

        {/* Flat button */}
        <Button
          variant={ButtonVariant.Secondary}
          onClick={() => toggleCategory("flat")}
          className={twMerge(
            "shrink-0 font-normal transition-opacity",
            !visibleCategories.flat && "opacity-50",
          )}
        >
          <span>{categoryStats.flat.count} flat</span>
        </Button>

        {/* Declining button */}
        <Button
          variant={ButtonVariant.Secondary}
          onClick={() => toggleCategory("declining")}
          className={twMerge(
            "shrink-0 font-normal transition-opacity",
            !visibleCategories.declining && "opacity-50",
          )}
        >
          <span className="inline-flex items-center">
            {categoryStats.declining.count}
            <IconArrowDownRight className="mx-0.5 h-3 w-3 text-red-500" />
            {categoryStats.declining.count > 0 &&
              ` (${categoryStats.declining.avgDelta} pts avg)`}
          </span>
        </Button>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Period dropdown (only for Last 30 days view) */}
        {showPeriodDropdown && (
          <SimpleSelect
            label="Period"
            isLabelHidden
            options={PERIOD_OPTIONS}
            value={selectedPeriod}
            onChange={handleSelectedPeriodChange}
          />
        )}
      </div>

      {/* Chart */}
      {isLoading ? (
        <div className="relative h-80 w-full">
          <ChartSkeleton />
        </div>
      ) : (
        <ScoreMovementScatterChart
          points={chartPoints}
          axisMin={axisMin}
          axisMax={axisMax}
          axisTicks={axisTicks}
          formatAxisLabel={(tick) => `${Math.round(tick * 100)}%`}
          xAxisTitle="Starting score"
          yAxisTitle="Current score"
        />
      )}
    </div>
  );
};
